import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const LABELS = { pets: ["pet", "pets"], invoices: ["invoice", "invoices"], payments: ["payment", "payments"], bookings: ["booking", "bookings"],
  memberships: ["membership", "memberships"], packages: ["package", "packages"], boarding: ["boarding stay", "boarding stays"],
  clinical: ["clinical record", "clinical records"] };

// Direct delete for users holding customers.delete (owner/admin). Others use the approval request button.
export function CustomerDeleteButton({ customer }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const check = useQuery({
    queryKey: ["customer-delete-check", customer.id], enabled: open,
    queryFn: async () => (await api.get(`/customers/${customer.id}/delete-check`)).data,
  });
  if (!hasPerm(user, "customers.delete") || customer.archived) return null;
  const linked = Object.entries(check.data?.linked || {}).filter(([, n]) => n > 0);
  const archive = check.data?.mode === "archive";

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/customers/${customer.id}`);
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["customer-stats"] });
      toast.success(data.mode === "archived" ? "Customer archived. All linked records were kept." : "Customer deleted.");
      navigate("/customers");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="cust-delete-btn"
        className="gap-1 text-rose-700 border-rose-200 hover:bg-rose-50"><Trash2 className="h-4 w-4" /> Delete</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-sm" data-testid="cust-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-rose-500" /> Delete {customer.name}?</DialogTitle>
          </DialogHeader>
          {check.isLoading ? <p className="text-sm text-slate-500">Checking linked records…</p> : archive ? (
            <div className="space-y-2 text-sm text-slate-600" data-testid="cust-delete-archive-note">
              <p>This customer has linked records, so they will be <b>archived</b> instead of erased:</p>
              <ul className="list-disc pl-5 text-xs">{linked.map(([k, n]) => <li key={k}>{n} {(LABELS[k] || [k, k])[n === 1 ? 0 : 1]}</li>)}</ul>
              <p className="text-xs text-slate-500">They disappear from customer lists. Financial, clinical and audit records stay unchanged.</p>
            </div>
          ) : <p className="text-sm text-slate-600" data-testid="cust-delete-hard-note">No linked records were found. The customer will be permanently deleted. The audit history is kept.</p>}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="min-h-[44px]">Cancel</Button>
            <Button onClick={run} disabled={busy || check.isLoading || check.isError} data-testid="cust-delete-confirm"
              className="min-h-[44px] bg-rose-600 hover:bg-rose-700 text-white">{busy ? "Working…" : archive ? "Archive customer" : "Delete customer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
